import getEnvVariable from './getEnvVariable';
import { getClientPortalBaseUrls, Converter } from '@itm/shared-frontend/lib/utils';

import {
  TargetPlatform,
  ClientPortalRole,
  UserStatus,
  PhoneNumberType,
  SelectOption,
  UserSQLPermissions,
  PaymentMethod,
  PensionType,
  BulkUploadAction,
  BulkUploadArea,
  SourceFileStatus,
  DataAccessDurationType,
  UserDataPermissionStatus,
  DataAccessRequestStatusDto,
  DataRetentionPolicyStatus,
  DataRetentionPolicyType,
  DataRetentionProductRuleType,
  DataRetentionPolicyApprovalStatus,
  DataRetentionAuditStatus,
  CompanyDefaultSchemeAccessAction,
  CompanyAuditArea,
  UserAuditArea,
  AdditionalSchemeType,
  PensionStatus,
  ThirdPartyPlatformSettingApp,
  AuthType,
  ProviderType,
  PgpKeyType,
  ReportType,
  RecipientType,
  EmailStatus,
  TemplateAuditChangeType,
  TrafficHttpMethod,
  TrafficResultGroup,
} from 'src/types';

export const ENV_PREFIX = 'REACT_APP_' as const;

export const ENVIRONMENT = getEnvVariable('ENVIRONMENT');
export const AUTH_API_URL = getEnvVariable('AUTH_API_URL');
export const TARGET_PLATFORM = getEnvVariable('TARGET_PLATFORM');
export const CLIENT_PORTAL_API_URL = getEnvVariable('CLIENT_PORTAL_API_URL');
export const DATA_GATEWAY_API_URL = getEnvVariable('DATA_GATEWAY_API_URL');
export const NOTIFICATION_API_URL = getEnvVariable('NOTIFICATION_API_URL');

export const isAzureTargetPlatform = TARGET_PLATFORM === TargetPlatform.AZURE;

export const EMPLOYERHUB_PRODUCT_ID = '4049e259-2c2c-4408-aa65-9868647fcd12';
export const FLEXPORTAL_PRODUCT_ID = '917408ef-adb9-411c-b0ce-794b2009c947';
export const EARCHIVE_LITE_PRODUCT_ID = '79efe713-c4ce-49ed-9b8f-2a841ea56953';

export const { clientPortalDomain } = getClientPortalBaseUrls(ENVIRONMENT);

export const getRoleWithoutPrefix = (role: string) => role.slice(role.indexOf('_') + 1);
const roleSortPriority = [
  getRoleWithoutPrefix(ClientPortalRole.SuperAdmin),
  getRoleWithoutPrefix(ClientPortalRole.Support),
  getRoleWithoutPrefix(ClientPortalRole.Analyst),
  getRoleWithoutPrefix(ClientPortalRole.Viewer),
  getRoleWithoutPrefix(ClientPortalRole.ClientAdmin),
  getRoleWithoutPrefix(ClientPortalRole.EmployerAdmin),
  getRoleWithoutPrefix(ClientPortalRole.Manager),
  getRoleWithoutPrefix(ClientPortalRole.CustomerOperations),
  getRoleWithoutPrefix(ClientPortalRole.SftpOnly),
] as const;

export const roleSortPrioritySortFn = (a: string, b: string) =>
  roleSortPriority.indexOf(getRoleWithoutPrefix(a)) - roleSortPriority.indexOf(getRoleWithoutPrefix(b));
export const userStatusOptions: SelectOption[] = Converter.enumToSelectOptions(UserStatus);
export const phoneNumberTypeOptions: SelectOption[] = [
  { label: PhoneNumberType.Main, value: PhoneNumberType.Main },
  { label: Converter.stripUpperToCapitalizedFirst(PhoneNumberType.NonUK), value: PhoneNumberType.NonUK },
  { label: PhoneNumberType.SMS, value: PhoneNumberType.SMS },
  { label: PhoneNumberType.Welsh, value: PhoneNumberType.Welsh },
  { label: PhoneNumberType.WhatsApp, value: PhoneNumberType.WhatsApp },
];
export const pensionTypeOptions: SelectOption[] = Converter.enumToSelectOptions(PensionType);
export const userSQLPermissionOptions: SelectOption[] = Converter.enumToSelectOptions(UserSQLPermissions);
export const paymentMethodOptions: SelectOption[] = Converter.enumToSelectOptions(PaymentMethod);
export const bulkUploadActionOptions: SelectOption[] = Converter.enumToSelectOptions(BulkUploadAction);
export const bulkUploadAreaOptions: SelectOption[] = Converter.enumToSelectOptions(BulkUploadArea);
export const sourceFileStatusOptions: SelectOption[] = Converter.enumToSelectOptions(SourceFileStatus);
export const dataAccessDurationTypeOptions: SelectOption[] = Converter.enumToSelectOptions(DataAccessDurationType);
export const userDataPermissionStatusOptions: SelectOption[] = Converter.enumToSelectOptions(UserDataPermissionStatus);
export const dataAccessRequestStatusOptions: SelectOption[] = Converter.enumToSelectOptions(DataAccessRequestStatusDto);
export const dataRetentionPolicyStatusOptions: SelectOption[] =
  Converter.enumToSelectOptions(DataRetentionPolicyStatus);
export const dataRetentionPolicyTypeOptions: SelectOption[] = Converter.enumToSelectOptions(DataRetentionPolicyType);
export const dataRetentionProductRuleTypeOptions: SelectOption[] =
  Converter.enumToSelectOptions(DataRetentionProductRuleType);
export const dataRetentionPolicyApprovalStatusOptions: SelectOption[] = Converter.enumToSelectOptions(
  DataRetentionPolicyApprovalStatus,
);
export const dataRetentionAuditStatusOptions: SelectOption[] = Converter.enumToSelectOptions(DataRetentionAuditStatus);
export const companyDefaultSchemeAccessActionOptions: SelectOption[] = Converter.enumToSelectOptions(
  CompanyDefaultSchemeAccessAction,
);
export const pgpKeyTypeOptions: SelectOption[] = Converter.enumToSelectOptions(PgpKeyType);
export const emailStatusOptions: SelectOption[] = Converter.enumToSelectOptions(EmailStatus);
export const templateAuditChangeTypeOptions: SelectOption[] = Converter.enumToSelectOptions(TemplateAuditChangeType);
export const trafficHttpMethodOptions: SelectOption[] = Converter.enumToSelectOptions(TrafficHttpMethod);
export const trafficResultGroupOptions: SelectOption[] = Converter.enumToSelectOptions(TrafficResultGroup).filter(
  ({ value }) => value !== TrafficResultGroup.Test,
);
export const companyAuditAreaOptions: SelectOption[] = [
  { label: 'Company details', value: CompanyAuditArea.Company },
  { label: 'Scheme details', value: CompanyAuditArea.Scheme },
  { label: 'Section details', value: CompanyAuditArea.Section },
  { label: 'Employer details', value: CompanyAuditArea.Employer },
  { label: 'Strategic partner details', value: CompanyAuditArea.StrategicPartner },
  { label: 'Product assignment', value: CompanyAuditArea.ProductAssignment },
  { label: 'Scheme product assignment', value: CompanyAuditArea.SchemeProductAssignment },
  { label: 'Bulk upload', value: CompanyAuditArea.BulkUpload },
];
export const companyAuditAreaOptionLabelByValueMap = new Map(
  companyAuditAreaOptions.map(({ value, label }) => [value, label]),
);

export const userAuditAreaOptions: SelectOption[] = [
  { label: 'User details', value: UserAuditArea.User },
  { label: 'SSH key', value: UserAuditArea.SshKey },
  { label: 'Products', value: UserAuditArea.Products },
  { label: 'Companies', value: UserAuditArea.Companies },
  { label: 'Employers', value: UserAuditArea.Employers },
];
export const userAuditAreaOptionLabelByValueMap = new Map(
  userAuditAreaOptions.map(({ value, label }) => [value, label]),
);

export const additionalSchemeTypeOptions: SelectOption[] = [
  { value: AdditionalSchemeType.FOR, label: 'Former' },
  { value: AdditionalSchemeType.OTH, label: 'Other' },
];
export const pensionStatusOptions: SelectOption[] = [
  { value: PensionStatus.IWU, label: 'Inactive and winding up' },
  { value: PensionStatus.IPPF, label: 'Inactive and PPF assessment' },
];
export const thirdPartyPlatformSettingAppOptions: SelectOption[] = [
  { value: ThirdPartyPlatformSettingApp.PenScope, label: 'PenScope' },
  { value: ThirdPartyPlatformSettingApp.LumeraCore, label: 'Lumera Core' },
  { value: ThirdPartyPlatformSettingApp.Untangler, label: 'Untangler' },
];
export const authMethodOptions: SelectOption[] = [
  { value: AuthType.Basic, label: 'Basic auth' },
  { value: AuthType.Bearer, label: 'Token based auth' },
];
export const providerTypeOptions: SelectOption[] = [
  { value: ProviderType.Azure, label: 'Azure AD' },
  { value: ProviderType.Okta, label: 'Okta' },
];

export const reportTypeOptions: SelectOption[] = [
  { value: ReportType.UserAccessSummary, label: 'User Access Report' },
  { value: ReportType.EmailLogs, label: 'Email Logs' },
  { value: ReportType.AccountEmailLogs, label: 'Account Email Logs' },
];

export const reportTypeLabelByValueMap = new Map(reportTypeOptions.map(({ value, label }) => [value, label]));

export const readonlyRecipientTypeLabelMap = {
  [RecipientType.Member]: 'Member',
};

export const recipientTypeLabelMap: Record<
  Exclude<RecipientType, RecipientType.Custom | keyof typeof readonlyRecipientTypeLabelMap>,
  string
> = {
  [RecipientType.ActionUser]: 'Action user',
  [RecipientType.UserRoleManager]: 'User role - Manager',
  [RecipientType.UserRoleManagerWithSftp]: 'User role - Manager with SFTP',
  [RecipientType.UserRoleCustomerOperations]: 'User role - Customer operations',
  [RecipientType.UserRoleCustomerOperationsWithSftp]: 'User role - Customer operations with SFTP',
  [RecipientType.UserRoleClientAdmin]: 'User role - Client admin',
  [RecipientType.UserRoleClientAdminWithSftp]: 'User role - Client admin with SFTP',
  [RecipientType.UserRoleAnalyst]: 'User role - Analyst',
  [RecipientType.UserRoleSupport]: 'User role - Support',
  [RecipientType.CompanyContactEmail]: 'Company contact email',
  [RecipientType.SchemeContactEmail]: 'Scheme contact email',
  [RecipientType.EmployerEmail]: 'Employer email',
};

export const apiAddressRegExp =
  /^(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)(\.(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)){3}$|^(([0-9a-fA-F]{1,4}:){7,7}[0-9a-fA-F]{1,4}|([0-9a-fA-F]{1,4}:){1,7}:|([0-9a-fA-F]{1,4}:){1,6}:[0-9a-fA-F]{1,4}|([0-9a-fA-F]{1,4}:){1,5}(:[0-9a-fA-F]{1,4}){1,2}|([0-9a-fA-F]{1,4}:){1,4}(:[0-9a-fA-F]{1,4}){1,3}|([0-9a-fA-F]{1,4}:){1,3}(:[0-9a-fA-F]{1,4}){1,4}|([0-9a-fA-F]{1,4}:){1,2}(:[0-9a-fA-F]{1,4}){1,5}|[0-9a-fA-F]{1,4}:((:[0-9a-fA-F]{1,4}){1,6})|:((:[0-9a-fA-F]{1,4}){1,7}|:)|fe80:(:[0-9a-fA-F]{0,4}){0,4}%[0-9a-zA-Z]{1,}|::(ffff(:0{1,4}){0,1}:){0,1}((25[0-5]|(2[0-4]|1{0,1}[0-9]){0,1}[0-9])\.){3,3}(25[0-5]|(2[0-4]|1{0,1}[0-9]){0,1}[0-9])|([0-9a-fA-F]{1,4}:){1,4}:((25[0-5]|(2[0-4]|1{0,1}[0-9]){0,1}[0-9])\.){3,3}(25[0-5]|(2[0-4]|1{0,1}[0-9]){0,1}[0-9]))$/;

/**
 * Synchronized with BE
 */
export const strongPasswordRegExp =
  /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[$@!%*?&£^()_+{}:~<>|=[;'#,./\-\]\\])[A-Za-z\d$@!%*?&£^()_+{}:~<>|=[;'#,./\-\]\\]{4,}$/;

/**
 * Synchronized with Equisoft
 */
export const phoneRegex = /^\+(?:[0-9]){1,3}\s(?:[0-9]){3,14}[0-9]$/;

/**
 * Synchronized with BE
 */
export const companiesHouseRegex = /^(?:\d{8}|[A-Za-z]{2}\d{6})$/;
